import { useState, useEffect } from "react";
import { MessageCircle, Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/utils/api";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [supportContact, setSupportContact] = useState({
    support_whatsapp: "+91-XXXXXXXXXX",
    support_telegram: "@uplino_support",
    support_email: "support@uplino.in"
  });

  useEffect(() => {
    // Fetch support contact from public settings
    const fetchSupportContact = async () => {
      try {
        const response = await api.get("/settings/support-contact");
        if (response.data) {
          setSupportContact(response.data);
        }
      } catch (error) {
        // Use defaults if not available
        console.log("Using default support contacts");
      }
    };
    fetchSupportContact();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post("/contact", formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try WhatsApp or Telegram instead.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <SEOHead 
        title="Contact Us | uplino.in - Get Support & Help"
        description="Contact uplino.in support team for any queries, technical help, or business inquiries. Available 24/7 via WhatsApp, Telegram, and email."
        keywords={["contact uplino", "uplino support", "customer service", "help desk"]}
        canonical="https://uplino.in/contact"
      />
      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="relative py-16 bg-gradient-to-br from-slate-900 via-slate-950 to-black">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent"></div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                Contact <span className="text-blue-400">Us</span>
              </h1>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Have questions? Need help? We're here for you 24/7. 
                Reach out to us through any of the channels below.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Options */}
        <section className="py-12 bg-slate-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              {/* WhatsApp */}
              <a 
                href={`https://wa.me/${supportContact.support_whatsapp?.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-slate-900/50 border border-white/10 rounded-xl p-6 hover:border-emerald-500/50 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-500/30 transition-colors">
                  <MessageCircle className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">WhatsApp</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Fastest way to reach us. Get instant support for your queries.
                </p>
                <p className="text-emerald-400 font-medium">{supportContact.support_whatsapp}</p>
              </a>

              {/* Telegram */}
              <a 
                href={`https://t.me/${supportContact.support_telegram?.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-slate-900/50 border border-white/10 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition-colors">
                  <Send className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Telegram</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Connect with us on Telegram for quick responses and updates.
                </p>
                <p className="text-blue-400 font-medium">{supportContact.support_telegram}</p>
              </a>

              {/* Email */}
              <a 
                href={`mailto:${supportContact.support_email || 'support@uplino.in'}`}
                className="group bg-slate-900/50 border border-white/10 rounded-xl p-6 hover:border-amber-500/50 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-500/30 transition-colors">
                  <Mail className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Email</h3>
                <p className="text-gray-400 text-sm mb-3">
                  For detailed inquiries and business partnerships.
                </p>
                <p className="text-amber-400 font-medium">{supportContact.support_email || 'support@uplino.in'}</p>
              </a>
            </div>

            {/* Contact Form & Info */}
            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Your Name
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-slate-800 border-white/10 text-white"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-slate-800 border-white/10 text-white"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Subject
                    </label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-slate-800 border-white/10 text-white"
                      placeholder="How can we help?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
                      placeholder="Tell us more about your query..."
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>

              {/* Additional Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Get in Touch</h2>
                  <p className="text-gray-400 leading-relaxed">
                    Whether you have a question about features, pricing, need a demo, 
                    or anything else, our team is ready to answer all your questions. 
                    We typically respond within 1-2 hours during business hours.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-rose-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-rose-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Support Hours</h3>
                      <p className="text-gray-400 text-sm">
                        24/7 - We're always available to help
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Response Time</h3>
                      <p className="text-gray-400 text-sm">
                        WhatsApp & Telegram: Within 30 minutes<br />
                        Email: Within 2-4 hours
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Location</h3>
                      <p className="text-gray-400 text-sm">
                        Operating across 270+ cities in India
                      </p>
                    </div>
                  </div>
                </div>

                {/* FAQ Link */}
                <div className="bg-slate-900/30 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Frequently Asked Questions
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Find quick answers to common questions about posting ads, 
                    payments, and account management.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="text-gray-400">• How to post my first ad?</li>
                    <li className="text-gray-400">• How does the wallet system work?</li>
                    <li className="text-gray-400">• What are time slots?</li>
                    <li className="text-gray-400">• How to boost my ad visibility?</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Contact;
